/**
 * Theme-independent scales (BUILD-PLAN 3.2): type, spacing, radius, motion,
 * layering, layout, and the three densities.
 */

export const FONT_STACKS = {
  ui: "'Inter Variable', 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
  mono: "'JetBrains Mono Variable', 'JetBrains Mono', ui-monospace, 'Cascadia Mono', monospace",
  /** Placeholder brand display face until the vector logo pack names one (BUILD-PLAN 3.2). */
  display: "'Inter Variable', 'Inter', system-ui, sans-serif",
} as const;

/** px. 11 is the floor and only for tertiary meta. */
export const TYPE_SCALE = [11, 12, 13, 14, 16, 18, 22, 28, 36] as const;

/** 4 px grid. Key is the token suffix (`--space-1-5` = 6px). */
export const SPACE: Record<string, number> = {
  '0': 0,
  '0-5': 2,
  '1': 4,
  '1-5': 6,
  '2': 8,
  '3': 12,
  '4': 16,
  '5': 20,
  '6': 24,
  '8': 32,
  '10': 40,
  '12': 48,
  '16': 64,
};

export const RADIUS: Record<string, string> = {
  sm: '4px',
  md: '6px',
  lg: '8px',
  xl: '12px',
  pill: '999px',
};

export const MOTION: Record<string, string> = {
  'duration-fast': '120ms',
  'duration-panel': '180ms',
  'duration-slow': '240ms',
  'ease-out': 'cubic-bezier(0.16, 1, 0.3, 1)',
  'ease-in-out': 'cubic-bezier(0.65, 0, 0.35, 1)',
};

export const Z: Record<string, number> = {
  base: 0,
  sticky: 10,
  nav: 20,
  topbar: 30,
  drawer: 40,
  dialog: 50,
  popover: 60,
  toast: 70,
  tooltip: 80,
};

export const LAYOUT: Record<string, string> = {
  'nav-w': '240px',
  'nav-w-collapsed': '56px',
  'topbar-h': '48px',
  'drawer-w': '480px',
  'drawer-w-sm': '420px',
  'drawer-w-lg': '560px',
  'measure-reading': '720px',
  'glass-blur': '14px',
};

export const DENSITIES = ['compact', 'comfortable', 'classic'] as const;
export type Density = (typeof DENSITIES)[number];
/** Compact (28 px rows) is the default (BUILD-PLAN 3.2). */
export const DEFAULT_DENSITY: Density = 'compact';

export const DENSITY_TOKENS: Record<Density, Record<string, string>> = {
  compact: {
    'row-h': '28px',
    'control-h': '28px',
    'control-h-sm': '24px',
    'control-h-lg': '34px',
    'font-base': '13px',
    'cell-px': '8px',
    'gap-density': '8px',
  },
  comfortable: {
    'row-h': '36px',
    'control-h': '32px',
    'control-h-sm': '28px',
    'control-h-lg': '40px',
    'font-base': '14px',
    'cell-px': '12px',
    'gap-density': '12px',
  },
  classic: {
    'row-h': '24px',
    'control-h': '26px',
    'control-h-sm': '22px',
    'control-h-lg': '30px',
    'font-base': '13px',
    'cell-px': '6px',
    'gap-density': '6px',
  },
};
